import type {
  ApiDashboardElement,
  Collection,
  DataSourceCandidatesResponse,
  WidgetDimensionsResponse,
  WidgetEntitiesResponse,
} from '@highimpact-frontend/api-types';
import {
  isWidgetDimensionsResponse,
  isWidgetEntitiesResponse,
} from '@highimpact-frontend/api-types';
import type { PlotTypes } from '@highimpact/dashboard-types';
import { dimensionSourceToSource } from '@highimpact/source-selector';

import type { DashboardHostContextValue } from '../context/DashboardHostContext.types';
import { coupledRowToSources } from '../dashboard/widget/widgets/shared/coupledDimensionSelection';
import {
  getCoupledWidgetDimensionConfig,
  getDefaultIndependentDimensionKeys,
} from '../dashboard/widget/widgets/shared/widgetDimensionSlots';
import {
  setElementCoupledSourcesFromResolvedSources,
  setElementDataSourcesFromResolvedSources,
  setElementEntityScopeFromWidgetEntity,
  setElementType,
  setPlotType,
  ToRemoteWidgetParams,
} from '../state/dashboard-element-operations';
import { buildWidgetEntityFromHost } from './formHandlerShared';

export const fullPathElementTypes: Exclude<
  ApiDashboardElement['element_type'],
  'collection' | 'readonlytext'
>[] = [
  'plot',
  'map',
  'table',
  'tablebycampaign',
  'text',
  'textfromentityproperty',
  'localizationfromcoordinates',
  'image',
  'imagegallery',
  'remotewidget',
];

export const partialPathElementTypes: Exclude<
  ApiDashboardElement['element_type'],
  'collection' | 'readonlytext'
>[] = ['text', 'remotewidget', 'image', 'imagegallery'];

export interface InitiatorCatalogBundle {
  readonly independentDimensions: DataSourceCandidatesResponse | null;
  readonly windroseDimensions: DataSourceCandidatesResponse | null;
  readonly ombrothermicDimensions: DataSourceCandidatesResponse | null;
  readonly entities: DataSourceCandidatesResponse | null;
}

function asDimensionsCatalog(
  catalog: DataSourceCandidatesResponse | null
): WidgetDimensionsResponse | null {
  return catalog && isWidgetDimensionsResponse(catalog) ? catalog : null;
}

function asEntitiesCatalog(
  catalog: DataSourceCandidatesResponse | null
): WidgetEntitiesResponse | null {
  return catalog && isWidgetEntitiesResponse(catalog) ? catalog : null;
}

function independentDimensionHasSources(
  catalog: WidgetDimensionsResponse
): boolean {
  return Object.values(catalog.independent_dimensions ?? {}).some(
    (sources) => sources.length > 0
  );
}

function coupledGroupsHaveRows(catalog: WidgetDimensionsResponse): boolean {
  return Object.values(catalog.coupled_groups ?? {}).some(
    (rows) => rows.length > 0
  );
}

/** Returns true when measure-point widgets can be seeded from the dimensions catalog. */
export function catalogSupportsFullPathElements(
  bundle: InitiatorCatalogBundle
): boolean {
  const independent = asDimensionsCatalog(bundle.independentDimensions);
  const windrose = asDimensionsCatalog(bundle.windroseDimensions);
  const ombrothermic = asDimensionsCatalog(bundle.ombrothermicDimensions);

  if (independent && independentDimensionHasSources(independent)) {
    return true;
  }
  if (windrose && coupledGroupsHaveRows(windrose)) {
    return true;
  }
  if (ombrothermic && coupledGroupsHaveRows(ombrothermic)) {
    return true;
  }

  return false;
}

/** Returns true when entity-scoped widgets can be seeded from the entities catalog. */
export function catalogSupportsPartialPathElements(
  bundle: InitiatorCatalogBundle
): boolean {
  const entities = asEntitiesCatalog(bundle.entities);
  return (entities?.entities.length ?? 0) > 0;
}

export function resolveAvailableInitiatorElementTypes(
  bundle: InitiatorCatalogBundle
): Exclude<ApiDashboardElement['element_type'], 'collection' | 'readonlytext'>[] {
  return catalogSupportsFullPathElements(bundle)
    ? fullPathElementTypes
    : partialPathElementTypes.filter((elementType) => {
        if (elementType === 'text' || elementType === 'remotewidget') {
          return true;
        }
        return catalogSupportsPartialPathElements(bundle);
      });
}

function pickDefaultIndependentSources(
  catalog: WidgetDimensionsResponse,
  widget:
    | 'timeseries'
    | 'histogram'
    | 'aggregation_table'
    | 'table_by_campaign',
  limit = 5
) {
  const dimensionKey = getDefaultIndependentDimensionKeys(widget)[0];
  if (!dimensionKey) {
    return [];
  }

  return (catalog.independent_dimensions?.[dimensionKey] ?? [])
    .slice(0, limit)
    .map((dimensionSource) => dimensionSourceToSource(dimensionSource));
}

function pickDefaultCoupledSources(
  catalog: WidgetDimensionsResponse,
  widget: 'windrose' | 'ombrothermic'
) {
  const { groupKey, dimensionOrder } = getCoupledWidgetDimensionConfig(widget);
  const firstRow = catalog.coupled_groups?.[groupKey]?.[0];
  if (!firstRow) {
    return [];
  }

  return coupledRowToSources(firstRow, dimensionOrder);
}

function seedEntityScopeFromHost(
  host: DashboardHostContextValue,
  element: ApiDashboardElement
): ApiDashboardElement {
  const entity = buildWidgetEntityFromHost(host);
  return entity
    ? setElementEntityScopeFromWidgetEntity(entity, element)
    : element;
}

function seedMeasurePointSources(
  sources: readonly ReturnType<typeof dimensionSourceToSource>[],
  element: ApiDashboardElement,
  options: { lastIsX?: boolean } = {}
): ApiDashboardElement {
  if (sources.length === 0) {
    return element;
  }

  return setElementDataSourcesFromResolvedSources(sources, element, options);
}

function seedCoupledPlotSources(
  widget: 'windrose' | 'ombrothermic',
  catalog: WidgetDimensionsResponse,
  element: ApiDashboardElement
): ApiDashboardElement {
  const sources = pickDefaultCoupledSources(catalog, widget);
  if (sources.length === 0) {
    return element;
  }

  const { dimensionOrder } = getCoupledWidgetDimensionConfig(widget);
  return setElementCoupledSourcesFromResolvedSources(
    sources,
    element,
    dimensionOrder
  );
}

export interface ApplyInitiatorSeedingOptions {
  readonly bundle: InitiatorCatalogBundle;
  readonly host: DashboardHostContextValue;
  readonly startDate: Date;
  readonly endDate: Date;
  readonly plugins: ToRemoteWidgetParams[];
  readonly preserveUnconfigured: boolean;
}

/**
 * Applies default catalog-backed data sources when the user picks an initiator element type.
 */
export function applyInitiatorSeeding(
  elementType: Exclude<
    ApiDashboardElement['element_type'],
    'collection' | 'readonlytext'
  >,
  config: ApiDashboardElement,
  options: ApplyInitiatorSeedingOptions
): Exclude<ApiDashboardElement, Collection> {
  const dimensions = asDimensionsCatalog(options.bundle.independentDimensions);
  const plotConfig = {
    start_date: options.startDate.toISOString(),
    end_date: options.endDate.toISOString(),
  };

  switch (elementType) {
    case 'plot': {
      const next = setElementType(
        elementType,
        config,
        options.preserveUnconfigured,
        plotConfig
      ) as Exclude<ApiDashboardElement, Collection>;
      if (!dimensions) {
        return next;
      }
      return seedMeasurePointSources(
        pickDefaultIndependentSources(dimensions, 'timeseries'),
        next
      ) as Exclude<ApiDashboardElement, Collection>;
    }
    case 'map': {
      const next = setElementType(elementType, config) as Exclude<
        ApiDashboardElement,
        Collection
      >;
      return seedEntityScopeFromHost(options.host, next) as Exclude<
        ApiDashboardElement,
        Collection
      >;
    }
    case 'table': {
      const next = setElementType(
        elementType,
        config,
        options.preserveUnconfigured,
        plotConfig
      ) as Exclude<ApiDashboardElement, Collection>;
      if (!dimensions) {
        return next;
      }
      return seedMeasurePointSources(
        pickDefaultIndependentSources(dimensions, 'aggregation_table'),
        next
      ) as Exclude<ApiDashboardElement, Collection>;
    }
    case 'tablebycampaign': {
      const next = setElementType(
        elementType,
        config,
        options.preserveUnconfigured,
        plotConfig
      ) as Exclude<ApiDashboardElement, Collection>;
      if (!dimensions) {
        return next;
      }
      return seedMeasurePointSources(
        pickDefaultIndependentSources(dimensions, 'table_by_campaign'),
        next
      ) as Exclude<ApiDashboardElement, Collection>;
    }
    case 'text':
      return setElementType(
        elementType,
        config,
        options.preserveUnconfigured
      ) as Exclude<ApiDashboardElement, Collection>;
    case 'textfromentityproperty': {
      const site_id = options.host.site_id ?? options.host.site?.id ?? 0;
      const next = setElementType(
        elementType,
        config as ApiDashboardElement,
        {
          entity_type: 'site',
          entity_id: site_id,
          entity_property: 'name',
        }
      ) as Exclude<ApiDashboardElement, Collection>;
      return seedEntityScopeFromHost(options.host, next) as Exclude<
        ApiDashboardElement,
        Collection
      >;
    }
    case 'localizationfromcoordinates':
      return setElementType(
        elementType,
        config
      ) as Exclude<ApiDashboardElement, Collection>;
    case 'image': {
      const next = setElementType(elementType, config) as Exclude<
        ApiDashboardElement,
        Collection
      >;
      return seedEntityScopeFromHost(options.host, next) as Exclude<
        ApiDashboardElement,
        Collection
      >;
    }
    case 'remotewidget':
      return setElementType(
        elementType,
        config,
        false,
        options.plugins[0] ?? {
          specificWidgetType: 'undefined',
          url: 'undefined',
        }
      ) as Exclude<ApiDashboardElement, Collection>;
    case 'imagegallery': {
      const next = setElementType(elementType, config) as Exclude<
        ApiDashboardElement,
        Collection
      >;
      return seedEntityScopeFromHost(options.host, next) as Exclude<
        ApiDashboardElement,
        Collection
      >;
    }
    default:
      return config as Exclude<ApiDashboardElement, Collection>;
  }
}

/**
 * Applies default catalog-backed sources when the user switches plot type in an initiator.
 */
export function applyPlotTypeSeedingFromCatalog(
  plotType: PlotTypes,
  config: ApiDashboardElement,
  bundle: InitiatorCatalogBundle
): ApiDashboardElement {
  const next = setPlotType(plotType, config);
  const independent = asDimensionsCatalog(bundle.independentDimensions);
  const windrose = asDimensionsCatalog(bundle.windroseDimensions);
  const ombrothermic = asDimensionsCatalog(bundle.ombrothermicDimensions);

  switch (plotType) {
    case 'timeseries':
      return independent
        ? seedMeasurePointSources(
            pickDefaultIndependentSources(independent, 'timeseries'),
            next
          )
        : next;
    case 'bar':
      return independent
        ? seedMeasurePointSources(
            pickDefaultIndependentSources(independent, 'histogram'),
            next
          )
        : next;
    case 'scatter':
      if (!independent) {
        return next;
      }
      return seedMeasurePointSources(
        [
          ...pickDefaultIndependentSources(independent, 'timeseries', 1),
          ...pickDefaultIndependentSources(independent, 'timeseries', 1),
        ],
        next,
        { lastIsX: true }
      );
    case 'windrose':
      return windrose
        ? seedCoupledPlotSources('windrose', windrose, next)
        : next;
    case 'ombrothermic':
      return ombrothermic
        ? seedCoupledPlotSources('ombrothermic', ombrothermic, next)
        : next;
    default:
      return next;
  }
}
